import { useEffect, useState } from 'react'
import { resolveFetchableImageUrl } from 'utils/imageUrl'

interface ImageBlobUrlResult {
  blobUrl: string | null
  loading: boolean
  error: string | null
}

export const useImageBlobUrl = (imageUrl: string): ImageBlobUrlResult => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!imageUrl) {
      setLoading(false)
      return
    }

    let objectUrl: string | null = null
    const abortController = new AbortController()

    const fetchImage = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch(resolveFetchableImageUrl(imageUrl), {
          signal: abortController.signal
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`)
        }

        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load image')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchImage()

    return () => {
      abortController.abort()
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [imageUrl])

  return { blobUrl, loading, error }
}
